import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Edit, Trash2, X, Save, Syringe } from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";

const empty = { name: "", default_interval_days: "", notes: "" };

export default function VaccineLibrary({ testid = "vaccine-library" }) {
  const { user } = useAuth();
  const role = user?.role;
  const canManage = ["admin", "manager", "super_admin"].includes(role);
  const canSee = role !== "groom" && role !== "driver";

  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/vaccine-types");
      setItems(data);
    } catch (e) { setError(formatApiError(e)); setItems([]); }
  };

  useEffect(() => { if (canSee) load(); }, [canSee]);

  if (!canSee) return null;

  const save = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const payload = {
        name: form.name.trim(),
        default_interval_days: Number(form.default_interval_days),
        notes: form.notes.trim() || null,
      };
      if (editingId) await api.patch(`/vaccine-types/${editingId}`, payload);
      else await api.post("/vaccine-types", payload);
      setShowForm(false); setEditingId(null); setForm(empty);
      await load();
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/vaccine-types/${confirmDel.id}`);
      setConfirmDel(null);
      await load();
    } catch (e) { setError(formatApiError(e)); setConfirmDel(null); }
  };

  return (
    <div className="se-card mt-6" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="se-label flex items-center gap-2"><Syringe size={12} /> Vaccines</div>
          <h3 className="font-heading mt-1" style={{ fontSize: 18 }}>Vaccine Library</h3>
          <p className="text-xs mt-1" style={{ color: "#8C9E8A" }}>Define the vaccine types your stable uses and their default interval. Per-horse overrides happen on the horse profile.</p>
        </div>
        {canManage && !showForm && (
          <button className="se-btn" onClick={() => { setForm(empty); setEditingId(null); setShowForm(true); }} data-testid={`${testid}-add-button`}>
            <Plus size={14} /> Add vaccine
          </button>
        )}
      </div>

      {error && <div className="mb-3 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid={`${testid}-error`}>{error}</div>}

      {showForm && (
        <form onSubmit={save} className="mb-4 p-3 rounded-lg" style={{ background: "rgba(74,124,124,0.08)" }} data-testid={`${testid}-form`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input className="se-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid={`${testid}-form-name`} />
            <input type="number" min={1} max={3650} className="se-input" placeholder="Default interval (days)" value={form.default_interval_days} onChange={(e) => setForm({ ...form, default_interval_days: e.target.value })} required data-testid={`${testid}-form-days`} />
            <input className="se-input" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid={`${testid}-form-notes`} />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" className="se-btn se-btn-ghost" onClick={() => { setShowForm(false); setEditingId(null); setForm(empty); }}><X size={14} /> Cancel</button>
            <button type="submit" className="se-btn" disabled={busy} data-testid={`${testid}-form-submit`}><Save size={14} /> {busy ? "Saving…" : editingId ? "Save" : "Add vaccine"}</button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {items === null && <div className="text-sm" style={{ color: "#8C9E8A" }}>Loading…</div>}
        {items !== null && items.length === 0 && !showForm && <div className="text-sm" style={{ color: "#8C9E8A" }} data-testid={`${testid}-empty`}>No vaccines yet.</div>}
        {(items || []).map((v) => (
          <div key={v.id} className="flex items-start gap-3 p-3 rounded-lg" style={{ background: "rgba(212,169,106,0.06)", border: "0.5px solid rgba(212,169,106,0.2)" }} data-testid={`${testid}-row-${v.id}`}>
            <div className="flex-1 min-w-0">
              <div style={{ fontWeight: 600 }}>{v.name}</div>
              <div className="text-xs mt-0.5" style={{ color: "#8C9E8A" }}>Default {v.default_interval_days} days{v.notes ? ` · ${v.notes}` : ""}</div>
            </div>
            {canManage && (
              <div className="flex gap-1">
                <button className="se-btn se-btn-ghost" style={{ padding: 6 }} onClick={() => { setEditingId(v.id); setForm({ name: v.name, default_interval_days: v.default_interval_days, notes: v.notes || "" }); setShowForm(true); }} data-testid={`${testid}-edit-${v.id}`} aria-label="Edit"><Edit size={12} /></button>
                <button className="se-btn se-btn-ghost" style={{ padding: 6 }} onClick={() => setConfirmDel(v)} data-testid={`${testid}-delete-${v.id}`} aria-label="Delete"><Trash2 size={12} /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      <ConfirmDialog open={!!confirmDel} onClose={() => setConfirmDel(null)} onConfirm={remove} title={`Delete '${confirmDel?.name}'?`} description="If any horse has this vaccine active you'll see a blocking warning instead." confirmLabel="Yes, delete" testid={`${testid}-confirm`} />
    </div>
  );
}
